import { useState, useCallback } from 'react';
import { validateCNPJ, validateCPF } from 'helpers/utils';

/**
 * Hook customizado para gerenciar validação de documentos (CNPJ/CPF)
 * 
 * @param {boolean} isCompany - Indica se é pessoa jurídica (CNPJ) ou física (CPF)
 * @returns {Object} Objeto com estado e funções de validação
 */
export const useDocumentValidation = (isCompany = false) => {
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationState, setValidationState] = useState({
    cnpj: { isValid: null, isComplete: false },
    cpf: { isValid: null, isComplete: false }
  });

  /**
   * Valida CNPJ em tempo real
   */
  const validateCNPJField = useCallback((value) => {
    const isComplete = value.length === 18; // CNPJ formatado tem 18 caracteres
    
    if (isComplete) {
      const isValid = validateCNPJ(value);
      setFieldErrors(prev => ({ ...prev, cnpj: !isValid }));
      setValidationState(prev => ({
        ...prev,
        cnpj: { isValid, isComplete: true }
      }));
      return isValid;
    } else if (value.length > 0) {
      // Campo preenchido mas incompleto - não mostra erro ainda
      setFieldErrors(prev => ({ ...prev, cnpj: false }));
      setValidationState(prev => ({
        ...prev,
        cnpj: { isValid: null, isComplete: false }
      }));
      return null;
    } else {
      // Campo vazio - remove erro
      setFieldErrors(prev => ({ ...prev, cnpj: false }));
      setValidationState(prev => ({
        ...prev,
        cnpj: { isValid: null, isComplete: false }
      }));
      return null;
    }
  }, []);

  /**
   * Valida CPF em tempo real
   */
  const validateCPFField = useCallback((value) => {
    const isComplete = value.length === 14; // CPF formatado tem 14 caracteres
    
    if (isComplete) {
      const isValid = validateCPF(value);
      setFieldErrors(prev => ({ ...prev, cpf: !isValid }));
      setValidationState(prev => ({
        ...prev,
        cpf: { isValid, isComplete: true }
      }));
      return isValid;
    } else if (value.length > 0) {
      // Campo preenchido mas incompleto - não mostra erro ainda
      setFieldErrors(prev => ({ ...prev, cpf: false }));
      setValidationState(prev => ({
        ...prev,
        cpf: { isValid: null, isComplete: false }
      }));
      return null;
    } else {
      // Campo vazio - remove erro
      setFieldErrors(prev => ({ ...prev, cpf: false }));
      setValidationState(prev => ({
        ...prev,
        cpf: { isValid: null, isComplete: false }
      }));
      return null;
    }
  }, []);

  /**
   * Handler genérico para mudança de valor do documento
   */
  const handleDocumentChange = useCallback((value, documentType) => {
    if (documentType === 'cnpj') {
      return validateCNPJField(value);
    } else if (documentType === 'cpf') {
      return validateCPFField(value);
    }
  }, [validateCNPJField, validateCPFField]);

  /**
   * Detecta erros de validação na resposta da API
   * 
   * @param {Object} errorResponse - Resposta de erro da API
   * @param {boolean} isCompany - Indica se é pessoa jurídica
   * @param {Object} currentValues - Valores atuais dos campos (cnpj, cpf)
   * @returns {Object} Objeto com os erros detectados
   */
  const detectApiErrors = useCallback((errorResponse, isCompany, currentValues = {}) => {
    const errors = {};
    const errorMessage = (errorResponse?.userMessage || '').toLowerCase();

    // Verifica se a mensagem menciona CNPJ ou CPF especificamente
    if (
      errorMessage.includes('cnpj') ||
      errorMessage.includes('cpf') ||
      errorMessage.includes('documento') ||
      errorMessage.includes('inválido')
    ) {
      // Se for mensagem genérica de campos inválidos, verifica o campo atual
      if (
        errorMessage.includes('inválido') &&
        !errorMessage.includes('cnpj') &&
        !errorMessage.includes('cpf')
      ) {
        // Mensagem genérica - marca o campo CNPJ/CPF se estiver preenchido
        if (isCompany && currentValues.cnpj) {
          errors.cnpj = true;
        } else if (!isCompany && currentValues.cpf) {
          errors.cpf = true;
        }
      } else {
        // Mensagem específica mencionando CNPJ ou CPF
        if (errorMessage.includes('cnpj') || (isCompany && errorMessage.includes('documento'))) {
          errors.cnpj = true;
        }
        if (errorMessage.includes('cpf') || (!isCompany && errorMessage.includes('documento'))) {
          errors.cpf = true;
        }
      }
    }

    // Verifica se há erros específicos de campos na resposta
    if (errorResponse?.errors) {
      if (errorResponse.errors.cnpj) errors.cnpj = true;
      if (errorResponse.errors.cpf) errors.cpf = true;
    }

    // Verifica se há um objeto de validação com campos específicos
    if (errorResponse?.validationErrors) {
      if (errorResponse.validationErrors.cnpj) errors.cnpj = true;
      if (errorResponse.validationErrors.cpf) errors.cpf = true;
    }

    // Se foi marcado manualmente qual campo deve ter erro
    if (errorResponse?._markCurrentField) {
      errors[errorResponse._markCurrentField] = true;
    }

    setFieldErrors(prev => ({ ...prev, ...errors }));
    return errors;
  }, []);

  /**
   * Limpa todos os erros
   */
  const clearErrors = useCallback(() => {
    setFieldErrors({});
    setValidationState({
      cnpj: { isValid: null, isComplete: false },
      cpf: { isValid: null, isComplete: false }
    });
  }, []);

  /**
   * Define erro manualmente (útil para erros da API)
   */
  const setError = useCallback((field, hasError) => {
    setFieldErrors(prev => ({ ...prev, [field]: hasError }));
  }, []);

  /**
   * Obtém o estado visual do campo baseado na validação
   */
  const getFieldVisualState = useCallback((value, documentType) => {
    const state = validationState[documentType];
    const hasApiError = fieldErrors[documentType];
    
    if (documentType === 'cnpj') {
      const isComplete = value.length === 18;
      const isValid = isComplete && validateCNPJ(value);
      const showError = hasApiError || (isComplete && !isValid);
      
      return {
        isInvalid: showError,
        isValid: isComplete && isValid && !hasApiError,
        showError,
        showSuccess: isComplete && isValid && !hasApiError
      };
    } else if (documentType === 'cpf') {
      const isComplete = value.length === 14;
      const isValid = isComplete && validateCPF(value);
      const showError = hasApiError || (isComplete && !isValid);
      
      return {
        isInvalid: showError,
        isValid: isComplete && isValid && !hasApiError,
        showError,
        showSuccess: isComplete && isValid && !hasApiError
      };
    }
    
    return {
      isInvalid: false,
      isValid: false,
      showError: false,
      showSuccess: false
    };
  }, [validationState, fieldErrors]);

  return {
    fieldErrors,
    validationState,
    validateCNPJField,
    validateCPFField,
    handleDocumentChange,
    detectApiErrors,
    clearErrors,
    setError,
    getFieldVisualState
  };
};
